import { dehydrate } from 'react-query';
import Layout from '@/components/global/Layout';
import { Hero } from '@/components/shared/Hero';
import { determinei18nLocale } from '@/helpers/determineLocale';
import { I18nLocales, ILocales } from '@/interfaces/locales.interfaces';
import client from '@/lib/apolloClient';
import { getBreadcrumbs } from '@/lib/getBreadcrumbs';
import { fetchGlobalData, getAllPages, getAllStories, getSingularPage } from '@/lib/strapiQueries';
import { GetStaticProps, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import dynamic from 'next/dynamic';
import { queryClient } from './_app';

const DynamicBlockBuilder = dynamic(() => import('@/components/blocks/BlockBuilder'));

export const getStaticPaths = async (context: GetStaticPropsContext) => {
    const locales = context.locales || [ILocales.en_NZ, ILocales.en_AU];
    const allPages: { locale: string; page: string }[] = [];

    for (const locale of locales) {
        if (locale === ILocales.default) {
            continue;
        }
        const pages = await getAllPages(client, determinei18nLocale(locale as ILocales));
        pages?.map((slug: string) => allPages.push({ locale: locale, page: slug }));
    }

    const paths = allPages.map(({ locale, page }) => {
        return {
            params: { slug: page.split('/') },
            locale,
        };
    });

    return {
        paths: paths,
        fallback: false,
    };
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
    const { params, locale, draftMode = false } = context;
    const cleanedLocale = locale !== ILocales.default ? determinei18nLocale(locale as ILocales) : I18nLocales.nz;

    const ifErrorProps = { props: {} };

    // global state data
    await queryClient.prefetchQuery(`global-${locale}`, () => fetchGlobalData(cleanedLocale), { staleTime: Infinity });
    await queryClient.prefetchQuery(`stories-${locale}`, () => getAllStories(cleanedLocale, draftMode), { staleTime: Infinity });

    const slug =
        Array.isArray(params?.slug) && params?.slug // Slug can be String[] (If multiple layers deep) or String
            ? params.slug[params.slug.length - 1] // If nested, we only need the last item in the array
            : (params?.slug as string); // Leave as is (String) & Homepage is "//"

    try {
        const page = await getSingularPage(client, cleanedLocale, slug, draftMode);

        if (!page) {
            return ifErrorProps;
        }

        return {
            props: {
                page: page[0],
                locale: locale,
                dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
                preview: draftMode,
            },
        };
    } catch (error) {
        return ifErrorProps;
    }
};

const Page = ({ page, locale, preview }: InferGetStaticPropsType<typeof getStaticProps>) => {
    if (!page || !global || !locale || locale === ILocales.default) {
        const Error = dynamic(() => import('@/components/global/Error'));
        return <Error statusCode={404} />;
    }
    const { hero, blocks } = page.attributes;
    const breadcrumbs = getBreadcrumbs(page);

    return (
        <Layout page={page} preview={preview}>
            {hero && <Hero {...hero} pageType={page.__typename} breadcrumbs={breadcrumbs} />}
            {blocks && <DynamicBlockBuilder blocks={blocks} />}
        </Layout>
    );
};

export default Page;
